import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarCheck, Clock, ChevronRight, ChevronLeft, AlertTriangle, Users2 } from "lucide-react";
import { AttendanceRegisterEditor, StatusPill } from "@/components/AttendanceQuickPickerDialog";
import WeaponSummary from "@/components/WeaponSummary";
import { AttendanceSkeleton } from "@/components/Skeleton";
import { toast } from "sonner";

function todayISO() { return new Date().toISOString().slice(0, 10); }
function shiftDay(dateStr, delta) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}
function labelDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const t = todayISO();
  const y = shiftDay(t, -1);
  const tomorrow = shiftDay(t, 1);
  if (dateStr === t) return "Oggi";
  if (dateStr === y) return "Ieri";
  if (dateStr === tomorrow) return "Domani";
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
}

export default function Attendance() {
  const [dateISO, setDateISO] = useState(todayISO());
  const [trainings, setTrainings] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [editRegisterId, setEditRegisterId] = useState(null);

  const loadDay = useCallback(async (d) => {
    try {
      const r = await api.get("/attendance/today", { params: { date: d } });
      setTrainings(r.data || []);
    } catch { setTrainings([]); }
  }, []);

  const loadSide = useCallback(async () => {
    try {
      const [a, h] = await Promise.all([
        api.get("/attendance/alerts"),
        api.get("/attendance/registers"),
      ]);
      setAlerts(a.data || []); setHistory(h.data || []);
    } catch { /* no-op */ }
  }, []);

  useEffect(() => { loadDay(dateISO); }, [dateISO, loadDay]);
  useEffect(() => { loadSide(); }, [loadSide]);

  const openTraining = async (t) => {
    try {
      let rid = t.register?.id;
      if (!rid) {
        const { data } = await api.post("/attendance", { training_id: t.training_id, date: dateISO });
        rid = data.id;
      }
      setEditRegisterId(rid);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const closeEditor = () => {
    setEditRegisterId(null);
    loadDay(dateISO); // refresh statuses
    loadSide();
  };

  // Siblings for quick switching from editor
  const siblings = useMemo(() => (trainings || []).map((t) => ({
    training_id: t.training_id,
    register_id: t.register?.id || null,
    title: t.title || t.group_name || "Allenamento",
    time: extractTime(t.date),
    status: t.status,
    weapons: t.weapons || [],
  })), [trainings]);

  const toBeDone = (trainings || []).filter((t) => t.status !== "completato");
  const done = (trainings || []).filter((t) => t.status === "completato");

  if (trainings === null) return <AttendanceSkeleton />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight flex items-center gap-3">
          <CalendarCheck className="w-8 h-8 text-[#0052FF]" />Presenze
        </h1>
        <p className="text-zinc-500 mt-1">Scegli un allenamento per aprire il registro con gli atleti previsti.</p>
      </div>

      {/* Date navigator */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid="date-prev"
          onClick={() => setDateISO(shiftDay(dateISO, -1))}
          className="p-2 rounded-md border border-zinc-200 hover:border-zinc-400 bg-white"
          aria-label="Giorno precedente"
        ><ChevronLeft className="w-4 h-4" /></button>
        <div className="px-3 py-2 rounded-md border border-zinc-200 bg-white min-w-[220px]">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Data</div>
          <div className="font-medium capitalize" data-testid="attendance-date-label">{labelDate(dateISO)}</div>
        </div>
        <button
          data-testid="date-next"
          onClick={() => setDateISO(shiftDay(dateISO, 1))}
          className="p-2 rounded-md border border-zinc-200 hover:border-zinc-400 bg-white"
          aria-label="Giorno successivo"
        ><ChevronRight className="w-4 h-4" /></button>
        {dateISO !== todayISO() && (
          <button
            data-testid="date-today"
            onClick={() => setDateISO(todayISO())}
            className="text-xs uppercase tracking-wider px-3 py-2 rounded-md border border-zinc-200 hover:border-zinc-400 bg-white text-zinc-600"
          >Torna a oggi</button>
        )}
        <input
          type="date"
          value={dateISO}
          onChange={(e) => e.target.value && setDateISO(e.target.value)}
          className="text-sm px-3 py-2 rounded-md border border-zinc-200 bg-white"
          data-testid="date-picker"
        />
      </div>

      <Tabs defaultValue="day">
        <TabsList>
          <TabsTrigger value="day" data-testid="tab-day">
            Allenamenti{toBeDone.length > 0 ? ` · ${toBeDone.length}` : ""}
          </TabsTrigger>
          {alerts.length > 0 && (
            <TabsTrigger value="alerts" data-testid="tab-alerts">Alert · {alerts.length}</TabsTrigger>
          )}
          <TabsTrigger value="history" data-testid="tab-history">Storico</TabsTrigger>
        </TabsList>

        <TabsContent value="day" className="pt-4 space-y-4">
          {trainings.length === 0 ? (
            <EmptyDay />
          ) : (
            <>
              <TrainingGroup
                title={toBeDone.length > 0 ? "Da registrare" : null}
                items={toBeDone}
                onOpen={openTraining}
              />
              {done.length > 0 && (
                <TrainingGroup
                  title="Completati"
                  items={done}
                  onOpen={openTraining}
                  muted
                />
              )}
            </>
          )}
        </TabsContent>

        {alerts.length > 0 && (
          <TabsContent value="alerts" className="pt-4">
            <div className="border border-zinc-200 rounded-md bg-white divide-y divide-zinc-100">
              {alerts.map((al) => (
                <div key={al.athlete_id} className="flex items-center gap-3 px-4 py-3" data-testid={`alert-${al.athlete_id}`}>
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{al.name || al.athlete_id}</div>
                    <div className="text-xs text-zinc-500">{al.absences} assenze negli ultimi 30 giorni</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        <TabsContent value="history" className="pt-4">
          <div className="border border-zinc-200 rounded-md bg-white divide-y divide-zinc-100">
            {history.length === 0 && <div className="p-6 text-sm text-zinc-500">Nessun registro storico.</div>}
            {history.slice(0, 50).map((r) => (
              <button key={r.id} onClick={() => setEditRegisterId(r.id)} data-testid={`history-${r.id}`}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 text-left transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{r.activity || r.date}</div>
                  <div className="text-xs text-zinc-500 font-mono">{r.date} · {r.entries?.length || 0} atleti</div>
                </div>
                <StatusPill status={r.status} />
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </button>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {editRegisterId && (
        <AttendanceRegisterEditor
          registerId={editRegisterId}
          siblings={siblings}
          onNavigate={openTraining}
          onClose={closeEditor}
          onSaved={() => { loadDay(dateISO); loadSide(); }}
        />
      )}
    </div>
  );
}

function extractTime(dateStr) {
  if (!dateStr) return "";
  const m = dateStr.match(/T?(\d{2}:\d{2})/);
  return m ? m[1] : "";
}

function TrainingGroup({ title, items, onOpen, muted }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="space-y-2">
      {title && (
        <div className={`text-[11px] uppercase tracking-wider font-semibold ${muted ? "text-zinc-400" : "text-zinc-600"}`}>
          {title} · {items.length}
        </div>
      )}
      <div className={`border rounded-md bg-white divide-y ${muted ? "border-zinc-100 divide-zinc-50 opacity-80" : "border-zinc-200 divide-zinc-100"}`}>
        {items.map((t) => (
          <button
            key={t.training_id}
            data-testid={`att-training-${t.training_id}`}
            onClick={() => onOpen(t)}
            className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-zinc-50 text-left transition-colors"
          >
            <Clock className="w-4 h-4 text-zinc-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{t.title || t.group_name || "Allenamento"}</div>
              <div className="text-xs text-zinc-500 font-mono mt-0.5">
                {extractTime(t.date) || t.date} · {t.duration || 90}′
                {t.coach_name ? ` · ${t.coach_name}` : ""}
                {typeof t.expected_count === "number" ? ` · ${t.expected_count} attesi` : ""}
              </div>
              {(t.weapons || []).length > 0 && (
                <div className="mt-1"><WeaponSummary weapons={(t.weapons || []).map((w) => ({ weapon_code: w, is_primary: true, status: "attiva" }))} /></div>
              )}
            </div>
            <StatusPill status={t.status} />
            <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyDay() {
  return (
    <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-md p-10 text-center space-y-2">
      <Users2 className="w-6 h-6 mx-auto text-zinc-300" />
      <div>Nessun allenamento pianificato per questa data.</div>
      <div className="text-xs">Crea un allenamento in <a className="underline" href="/allenamento">Allenamento</a>.</div>
    </div>
  );
}
